import { Subscribable, Subscription } from '@benzinga/subscribable';
import { SafePromise } from '@benzinga/safe-await';
import {
  Card,
  CheckoutSubscription,
  Coupon,
  Product,
  IPrecheckoutRequest,
  ILineItemPrice,
  IPrecheckoutResponse,
} from './entities';
import { ShopRequest, ShopRequestEvent } from './request';
import { Session } from '@benzinga/session';
import { ShopStore } from './store';

export type ShopManagerEvent = ShopRequestEvent;

export class ShopManager extends Subscribable<ShopManagerEvent> {
  private request: ShopRequest;
  private requestSubscription?: Subscription<ShopRequest>;
  private store: ShopStore;

  constructor(session: Session) {
    super();
    this.store = new ShopStore();
    this.request = new ShopRequest(session);
  }

  public static getName = () => 'benzinga-shop';
  public static getEnvironment = (env: Record<string, string>) => ({
    url: new URL(env.url ?? 'https://accounts.benzinga.com/api/v1/'),
  });

  public getProduct = (productName: string): SafePromise<Product> => {
    return this.request.getProduct(productName);
  };

  public getProducts = (): SafePromise<Product[]> => {
    return this.request.getProducts();
  };

  public preCheckout = (body: IPrecheckoutRequest): SafePromise<IPrecheckoutResponse> => {
    return this.request.preCheckout(body);
  };

  public upgradePlan = (
    selected_card: string,
    selected_plan: string,
    subscription_uuid: string,
  ): SafePromise<ILineItemPrice> => {
    return this.request.upgradePlan(selected_card, selected_plan, subscription_uuid);
  };

  public retrieveCoupon = (code: string): SafePromise<Coupon> => {
    return this.request.getCouponsCode(code);
  };

  public checkout = (
    lineItems: string[],
    plan: string,
    token: string,
    couponCode?: string,
    source?: string,
    will_activate_on?: string,
    subscription?: string,
  ): SafePromise<CheckoutSubscription> =>
    this.request.checkout({
      coupon: couponCode,
      line_items: lineItems,
      plan,
      source,
      subscription,
      token,
      will_activate_on,
    });

  public cartBail = async (planSlug: string, submitted = false): SafePromise<undefined> => {
    return this.request.cartBail(planSlug, submitted);
  };

  public getCreditCards = async (): SafePromise<Card[]> => {
    const cards = await this.request.getCreditCards();
    if (cards.ok) {
      this.store.updateCards(cards.ok);
    }
    return cards;
  };

  public getStoredCreditCards = () => this.store.getCards();

  public addCreditCard = (token: string): SafePromise<Card> => {
    return this.request.addCreditCard(token);
  };

  public getStoredCreditCard = (id: string): Card | undefined => {
    return this.store.getCards()?.find(c => c.uuid === id);
  };

  public deleteCreditCard = async (id: string): SafePromise<undefined> => {
    const deleted = await this.request.deleteCreditCard(id);
    this.getCreditCards();
    return deleted;
  };

  protected onFirstSubscription(): void {
    this.requestSubscription = this.request.listen(event => this.dispatch(event));
  }

  protected onZeroSubscriptions(): void {
    this.requestSubscription?.unsubscribe();
  }
}
